import { err, Result } from '@easeltv/fail';
import { ZodType, ZodTypeDef } from 'zod';

import { RequestAbortedFail } from '../errors';
import { RequestMethod, RequestOptions, RequestResult } from '../utils/types';
import { makeRequest } from './makeRequest';
import { makeUrl } from './makeUrl';
import { parseArrayBuffer } from './parseArrayBuffer';
import { parseResponse } from './parseResponse';
import { validateResponse } from './validateResponse';

export const abort = (
    abortController: AbortController,
): Promise<Result<never, RequestAbortedFail>> =>
    new Promise((resolve) => {
        abortController.signal.addEventListener('abort', () => {
            resolve(
                err(
                    new RequestAbortedFail({
                        key: 'remote.request.aborted',
                        description: 'The request has been cancelled',
                        name: 'Request Cancelled',
                    }),
                ),
            );
        });
    });

/**
 * baseRequest
 *
 * Fetches the endpoint and returns a full validated response
 *
 * @param method HTTP request method
 * @param endpoint Location to be fetched
 * @param options Object containing params and queries
 * @param headers Any headers to send with the request
 * @param validator What to validate the returned data against
 * @param body Body to send with the request
 */
export const baseRequest = async <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    method: RequestMethod,
    endpoint: string,
    baseUrl: string,
    options: RequestOptions,
    headers: Record<string, string>,
    validator: ZodType<Output, Def, Input>,
    body?: Record<string, unknown>,
    abortController?: AbortController,
): Promise<RequestResult<Output>> => {
    // Construct the URL string
    const url = makeUrl(endpoint, baseUrl, options);

    // If no AbortController is given, we create one to fill the gap
    const ab = abortController || new AbortController();

    // Make the request, race again the abort controller
    const responseResult = await Promise.race([
        makeRequest(method, url, ab, headers, body),
        abort(ab),
    ]);

    if (!responseResult.ok) return responseResult;

    // Parse the raw response into JSON
    const nonValidatedResponseResult = await parseResponse(
        responseResult.payload,
    );

    // If the parsing fails, return the error
    if (!nonValidatedResponseResult.ok) return nonValidatedResponseResult;

    // Validate the JSON data and return the validated response or error.
    return validateResponse(nonValidatedResponseResult.payload, validator);
};

/**
 * binaryRequest
 *
 * Fetches a binary from the given endpoint
 *
 * @param method HTTP request method
 * @param endpoint Location to be fetched
 * @param options Object containing params and queries
 * @param headers Any headers to send with the request
 * @param body Body to send with the request
 * @param body An instance of the AbortController
 */
export const binaryRequest = async (
    method: RequestMethod,
    endpoint: string,
    baseUrl: string,
    options: RequestOptions,
    headers: Record<string, string>,
    body?: Record<string, unknown>,
    abortController?: AbortController,
): Promise<RequestResult<ArrayBuffer>> => {
    // Construct the URL string
    const url = makeUrl(endpoint, baseUrl, options);

    // If no AbortController is given, we create one to fill the gap
    const ab = abortController || new AbortController();

    // Make the request, race again the abort controller
    const responseResult = await Promise.race([
        makeRequest(method, url, ab, headers, body),
        abort(ab),
    ]);

    if (!responseResult.ok) return responseResult;

    // Parse the raw response into JSON
    return parseArrayBuffer(responseResult.payload);
};

/**
 * fileRequest
 *
 * Fetches the endpoint and returns a full validated response
 *
 * @param method HTTP request method
 * @param endpoint Location to be fetched
 * @param options Object containing params and queries
 * @param headers Any headers to send with the request
 * @param validator What to validate the returned data against
 * @param body Body to send with the request
 */
export const fileRequest = async <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    method: RequestMethod,
    endpoint: string,
    baseUrl: string,
    options: RequestOptions,
    headers: Record<string, string>,
    validator: ZodType<Output, Def, Input>,
    body?: File,
    abortController?: AbortController,
): Promise<RequestResult<Output>> => {
    // Construct the URL string
    const url = makeUrl(endpoint, baseUrl, options);

    // If no AbortController is given, we create one to fill the gap
    const ab = abortController || new AbortController();

    // Make the request, race again the abort controller
    const responseResult = await Promise.race([
        makeRequest(method, url, ab, headers, body),
        abort(ab),
    ]);

    if (!responseResult.ok) return responseResult;

    // Parse the raw response into JSON
    const nonValidatedResponseResult = await parseResponse(
        responseResult.payload,
    );

    // If the parsing fails, return the error
    if (!nonValidatedResponseResult.ok) return nonValidatedResponseResult;

    // Validate the JSON data and return the validated response or error.
    return validateResponse(nonValidatedResponseResult.payload, validator);
};
